import { useRef } from 'react';

interface IdempotencyAttempt {
  fingerprint: string;
  key: string;
}

// Callers MUST include a target discriminator (e.g. the appointment id) in the
// commandInputs passed to keyFor. Without it, two different appointments that
// share the same participant IDs, versions, scope, and audit inputs would produce
// the same fingerprint and reuse the same idempotency key, and the server's
// appointment-scoped canonical digest would then reject the retry with a
// non-terminal 409 idempotency conflict.
export function useStableIdempotencyKey(prefix: string, principalId: string) {
  const attempt = useRef<IdempotencyAttempt | null>(null);
  const storageKey = `moments:idempotency:${principalId}:${prefix}`;
  const activeStorageKey = useRef(storageKey);
  if (activeStorageKey.current !== storageKey) {
    activeStorageKey.current = storageKey;
    attempt.current = null;
  }
  const controls = useRef({
    keyFor(commandInputs: object): string {
      const fingerprint = stableStringify(commandInputs);
      if (attempt.current?.fingerprint === fingerprint) return attempt.current.key;

      const attempts = readAttempts(activeStorageKey.current);
      const key = attempts[fingerprint] ?? `${prefix}:${crypto.randomUUID()}`;
      attempts[fingerprint] = key;
      writeAttempts(activeStorageKey.current, attempts);
      attempt.current = { fingerprint, key };
      return attempt.current.key;
    },
    markTerminal() {
      if (!attempt.current) return;
      const attempts = readAttempts(activeStorageKey.current);
      delete attempts[attempt.current.fingerprint];
      writeAttempts(activeStorageKey.current, attempts);
      attempt.current = null;
    },
  });

  return controls.current;
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.entries(value)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => `${JSON.stringify(key)}:${stableStringify(entry)}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

function readAttempts(storageKey: string): Record<string, string> {
  const stored = sessionStorage.getItem(storageKey);
  if (!stored) return {};
  try {
    const attempts: unknown = JSON.parse(stored);
    return attempts && typeof attempts === 'object' && !Array.isArray(attempts)
      ? (attempts as Record<string, string>)
      : {};
  } catch {
    return {};
  }
}

function writeAttempts(storageKey: string, attempts: Record<string, string>) {
  if (Object.keys(attempts).length === 0) {
    sessionStorage.removeItem(storageKey);
    return;
  }
  sessionStorage.setItem(storageKey, JSON.stringify(attempts));
}
